import { _ } from "../../../polyglot/polyglot.js";
import { formatAsMonthAndYear } from "../../../helpers/date-formatting-util.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import AbstractImporter from "../AbstractImporter.js";
import AlertCollector from "../../../alerts/AlertCollector.js";
import DateRangePickerStep from "../DateRangePickerStep.js";
import DropdownSelectionStep from "../DropdownSelectionStep.js";
import SaftIntegration from "../../../../models/integration/SaftIntegration.js";
import session from "../../../session.js";
import Timespan from "../../../../models/period/Timespan.js";
import UploadFileStep from "../UploadFileStep.js";
import UploadService from "../../../services/UploadService.js";

/**
 * `SaftImporter` imports from Saft integration API.
 */
export default class SaftImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		this._saftImportRepository = makeMonitorRepository({
			route: "/Shared/Import/Saft",
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	start(...args) {
		super.start(...args);

		this._uploadService.setUploadPath(
			`/Shared/Import/SaftUpload/${this.getAgencyId()}`,
		);
	}

	_wizardSteps() {
		return [
			this._uploadSaftFile(),
			this._selectFiscalYears(),
			this._editFiscalYear(),
		];
	}

	// Step 1: Let the user select a Saft file to be uploaded
	_uploadSaftFile() {
		this._uploadService = new UploadService();

		return new UploadFileStep({
			stepText: _(
				"Click on 'Choose a SAF-T file' and browse on your computer or file-server to choose a SAF-T file that you want to import.",
			),
			uploadButtonLabel: _("Choose a SAF-T file"),
			onUpload: this._uploadService.uploadFile.bind(this._uploadService),
			validate: this._validateFile.bind(this),
			submitCallback: this._getFiscalYears.bind(this),
		});
	}

	_validateFile(file) {
		const validation = new AlertCollector();
		if (!file.name.endsWith(".xml")) {
			validation.addError(
				_("File not valid. You must select an .xml file"),
			);
		}
		return validation;
	}

	// Step 1 ⇒ 2: Set the corresponding company fiscal years or an empty array if none.
	_getFiscalYears({ onDone, uploadResponse }) {
		this._context.fiscalYears = uploadResponse.years || [];
		onDone();
	}

	// Step 2: Select the fiscal year amongst existing ones or set a new one.
	_selectFiscalYears() {
		return new DropdownSelectionStep({
			instructions: _("Select a fiscal year"),
			dropdownLabel: _("Fiscal years"),
			getItemsFromContext: (context) =>
				this._makeFiscalYearsDropdownItems(context.fiscalYears),
			submitCallback: this._setSelectedFiscalYear.bind(this),
		});
	}

	_makeFiscalYearsDropdownItems(fiscalYears) {
		let items = fiscalYears.map((each) => ({
			label: `${formatAsMonthAndYear(
				each.start,
			)} - ${formatAsMonthAndYear(each.end)}`,
			value: each,
		}));

		items = [
			{ label: _("Add a new fiscalYear"), value: { id: null } },
			...items,
		];

		items.at(-1).selected = true;
		return items;
	}

	// Step 2 ⇒ 3: define the chosen fiscal year or defaults to the current year.
	_setSelectedFiscalYear({ onDone, selection }) {
		if (selection?.id) {
			const start = new Date(selection.start);
			const end = new Date(selection.end);
			this._context.fiscalYearId = selection.id;
			this._context.startYear = start.getFullYear();
			this._context.startMonth = start.getMonth();
			this._context.endYear = end.getFullYear();
			this._context.endMonth = end.getMonth();
		} else {
			const currentYear = new Date().getFullYear();
			this._context.fiscalYearId = null;
			this._context.startYear = currentYear;
			this._context.startMonth = 0;
			this._context.endYear = currentYear;
			this._context.endMonth = 11;
		}
		onDone();
	}

	// Step 3: Manually define the fiscal year if needed
	_editFiscalYear() {
		return new DateRangePickerStep({
			instructions: _("Set the appropriate fiscal year"),
			submitCallback: this._postImport.bind(this),
			getDateRangeFromContext: (context) =>
				new Timespan({
					start: new Date(context.startYear, context.startMonth, 1),
					// using the previous day from the next month to get the correct last day number
					end: new Date(context.endYear, context.endMonth + 1, 0),
				}),
			shouldSubmitOnStartFromContext: (context) => context.fiscalYearId,
		});
	}

	// Step 3 ⇒ 4: Save all the data coresponding to the selected fiscal year.
	_postImport({ onDone, step, dateRange }) {
		this._saftImportRepository
			.save({
				notify: false,
				model: {
					fiscalYearId: this._context.fiscalYearId,
					startYear: dateRange.getStart().getFullYear(),
					startMonth: dateRange.getStart().getMonth() + 1,
					endYear: dateRange.getEnd().getFullYear(),
					endMonth: dateRange.getEnd().getMonth() + 1,
				},
			})
			.then((data) => {
				this._context.importData = data;
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(
					this._errorCodeToMessage(error.responseJSON.message),
				);

				this._context.fiscalYearId = null;
				onDone();
			});
	}

	_errorCodeToMessage(errorCode) {
		return errorCode;
	}
}

SaftImporter.integrationType = SaftIntegration.getType();
